import {useStore} from '..';
import {APP_MODULE} from '.';
import {mainModule, StoreGetters, StoreActions} from './main.module';
import {FilterTag, TripOption, SuggestionGroup, SuggestionPois} from '@/views/amex/app-types';
import {
  ProblemDestination,
  RecommendationContext,
  TripAction,
  SavedTrip,
  AlternativesCollection,
  SolutionUpdateDetails,
  PriceWithExplanation,
  ProblmSubmissionRoute,
  NearbyTripOptions,
  FallbackImgs,
  BudgetPreferences,
  IntersectionalFilter,
} from './global.types';
import axios from 'axios';
import {v4} from 'uuid';
import {Dictionary} from 'vue-router/types/router';
import {
  ISolution,
  Price,
  CurrencyCode,
  UserActivityCategoryEnum,
  Activity,
  CandidateTransportationItem,
  CandidatePoiItem,
} from '@mobi-components/global-solution';
import {ExtendedSolutionResponse, SolutionResponse, SolutionSummary} from '../solution/solution-support.types';
import {Trip, TripsWithExplanations, TripWithPois} from '../api/download-destination-trips';
import {IGetAlternativesResponse, SelectOptions} from '../api/load-alternatives';
import {BudgetFilterValue} from '@mobi-components/amex-bluesky-components';
import {IRescheduleActivityRequest, LunchTime} from '../api/reschedule-activity';
import {POISearchOption} from '../api/search-poi';
import {TravelersFilterValue} from '@common/travelers-filter/travelers.types';
import {TravelersIndormation} from '../api/submit-amex-problem';
import {IGetAlternativeRecommendationsResponse} from '../api/load-alternative-recommendations';
import {AmexProblem, IntersectionalFiltersDomain} from '../api/build-problem';

const store = useStore();

export function registerStoreMainModule() {
  // @ts-ignore
  store.registerModuleOnce(APP_MODULE, mainModule);
}

export function getSolution(): ISolution {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION}`];
}
export function getSolutionStartEndValues(): {start: number; end: number} {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_START_END_VALUES}`];
}
export function getStartEndDates(): {start: number; end: number} {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_START_END_DATES}`];
}
export function loadAvailableDomains(context = ''): Promise<FilterTag[]> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_DOMAIN_TAGS}`, context);
}
export function dropContext(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SELECT_TRIP}`, '');
}
export function dropSelections(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_SELECTIONS}`);
}
export function resetFilters(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.RESET_FILTERS}`);
}
export function getTripContext(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_CONTEXT}`];
}
export function getContextTimezone(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_CONTEXT_TIMEZONE}`];
}
export function getSessionId(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SESSION_ID}`];
}
export function getSolutionCost(): Price[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_COST}`];
}
export function getSolutionContext(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_CONTEXT}`] || '';
}
export function getSolutionContexts(): CandidatePoiItem[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_CONTEXTS}`];
}
export function toggleSolutionTags(tags: FilterTag[]): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.TOGGLE_SOLUTION_TAGS}`, tags);
}
export function toggleTripType(type: string) {
  return store.dispatch(`${APP_MODULE}/${StoreActions.TOGGLE_TRIP_TYPE}`, type);
}
async function getTimezone(lat: number, lng: number): Promise<string> {
  interface TimeZoneResponse {
    dstOffset: number;
    rawOffset: number;
    timeZoneId: string;
    timeZoneName: string;
    status: google.maps.places.PlacesServiceStatus;
    errorMessage: string;
  }

  const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const response = await axios.get<TimeZoneResponse>(`https://maps.googleapis.com/maps/api/timezone/json`, {
    params: {
      location: `${lat},${lng}`,
      timestamp: Math.floor(new Date().valueOf() / 1000),
      key: process.env.VUE_APP_GOOGLE_API_GEOCODE_KEY,
    },
  });
  if (response.data) {
    return response.data.timeZoneId;
  }
  return defaultTimeZone;
}

async function mapSearchResultIntoProblemDestination(value: POISearchOption | null) {
  if (!value) {
    return null;
  }
  const lat = value.lat || 0;
  const lng = value.lng || 0;
  const timezone = await getTimezone(lat, lng);

  return {
    name: value.name || '',
    id: v4(),
    lat,
    lng,
    time_zone: timezone,
    allowed_modes: [],
    type: value.type || '',
  };
}

export async function updateOrigin(value: POISearchOption | null): Promise<void> {
  const origin = await mapSearchResultIntoProblemDestination(value);
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_ORIGIN}`, origin);
}
export async function updateDestination(value: POISearchOption | null): Promise<TripOption[]> {
  const destination = await mapSearchResultIntoProblemDestination(value);
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_DESTINATION}`, destination);
}
export async function saveDestination(value: ProblemDestination): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_DESTINATION}`, value);
}
export function getOrigin(): ProblemDestination | null {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ORIGIN}`];
}
export function getDestination(): ProblemDestination | null {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DESTINATION}`];
}
export async function selectTrip(tripId: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SELECT_TRIP}`, tripId);
}
export function getTripId(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_ID}`] || '';
}
export async function loadRecommendationDetails(
  route: ProblmSubmissionRoute,
  contextName: string,
  sort = SelectOptions.best_match,
): Promise<Dictionary<RecommendationContext[]> | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_RECOMMENDATION_DETAILS}`, {
    route,
    sort,
    contextName,
  });
}
export function getIsRecommendationsLoading(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_IS_RECOMMENDATIONS_LOADING}`] || false;
}
export async function loadTripOptions(
  route: ProblmSubmissionRoute,
  trip: ProblemDestination,
  includeFlights = true,
): Promise<TripsWithExplanations> {
  if (!trip) {
    return {
      trips: [],
      explanations: [],
      recommendations: [],
    };
  }
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_TRIP_OPTIONS}`, {
    route,
    trip,
    includeFlights,
  });
}
export async function dropTripOptions(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_TRIP_OPTIONS}`);
}
export async function loadTripOptionsByProblem(problem: AmexProblem, tripId: string): Promise<TripsWithExplanations> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_TRIP_OPTIONS_BY_PROBLEM}`, {problem, tripId});
}
// updates solution via API with new preferences without re-submitting problem
export async function updateTripPreferences(): Promise<TripsWithExplanations> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_TRIP_PREFERENCES}`);
}
// updates solution via API with new travelers info without re-submitting problem
export async function updateTripTravelersInfo(): Promise<TripsWithExplanations> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_TRIP_TRAVELERS}`);
}
export async function loadNearbyOptions(): Promise<NearbyTripOptions | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_NEARBY_PLACES}`);
}
export async function toggleFlightOrStay(
  tripId: string,
  flight: boolean,
  stay: boolean,
): Promise<SolutionResponse | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.TOGGLE_FLIGHT_OR_STAY}`, {flight, stay, tripId});
}
export function getFirstDestination(): TripOption | null {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_FIRST_DEST}`];
}
export function getDestinationOptions(): Record<string, Trip[]> {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DEST_OPTIONS}`];
}
export function getNearbyLocations(): TripOption[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_NEARBY_LOCATIONS}`];
}
export function getDestLocations(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_IS_DEST_AVAILABLE}`];
}
export function getRecommendationDetails(): Dictionary<SuggestionPois[]> {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_RECOMMENDATION_DETAILS}`];
}
export function getFavorites(tripId: string): RecommendationContext[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_FAVORITE_TRIPS}`](tripId) || [];
}
export function addFavorite(value: TripAction[]): Promise<string> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.ADD_FAVORITE}`, value);
}
export function dropFavorites(tripId?: string): Promise<string> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_FAVORITES}`, tripId);
}
export async function updateSolution(
  tripId: string,
  route: ProblmSubmissionRoute,
  flights: CandidateTransportationItem[] = [],
  mealPrefference: LunchTime = LunchTime.NONE,
  additionalContexts: CandidatePoiItem[] = [],
): Promise<SolutionResponse | null> {
  store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTON_LOADING_STATE}`, true);
  return store.dispatch(`${APP_MODULE}/${StoreActions.SUBMIT_PROBLEM}`, {
    tripId,
    route,
    flights,
    mealPrefference,
    additionalContexts,
  });
}
export async function saveSolution(
  tripId: string,
  solutionResponse: SolutionResponse,
): Promise<SolutionResponse | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SAVE_SOLUTION}`, {
    tripId,
    result: solutionResponse,
  });
}
export function getAmexSolution(): ISolution | null {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION}`];
}
export function getSolutionLoading(): ISolution | null {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_LOADING}`];
}
export function getAllSelectedFilters(): FilterTag[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ALL_SELECTED_FILTERS}`];
}
export function getSelectedGeo(): string[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SELECTED_GEO}`];
}
export function getTripName(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_NAME}`];
}
export function downloadAlternatives(
  activityId: string,
  sort = SelectOptions.best_match,
): Promise<IGetAlternativesResponse | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_ALTERNATIVES}`, {activityId, sort});
}
export function downloadRecommendationAlternatives(
  activityId: string,
): Promise<IGetAlternativeRecommendationsResponse | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_RECOMMENDATION_ALTERNATIVES}`, {activityId});
}
export function getAllAlternatives(): Record<string, AlternativesCollection> {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ALL_ALTERNATIVES}`];
}
export function selectAlternative(activityId: string, activity: Activity): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SELECT_ALTERNATIVE}`, {
    activity,
    activityId,
  });
}
export function getMyTrips(): SavedTrip[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
}
export function getPoiAlternatives(id: string): AlternativesCollection {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ALTERNATIVES}`](id);
}
export function fetchMyTrips() {
  return store.dispatch(`${APP_MODULE}/${StoreActions.FETCH_MY_TRIPS}`);
}
export function dropMyTrips() {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_MY_TRIPS}`);
}
export function getUsedFilters(): string[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_USED_FILTERS}`] || [];
}
export function isSolutionUnderconstrained(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.IS_SOLUTION_UNDERCONSTRAINED}`] || false;
}
export function getAllRecommendationsContent(): RecommendationContext[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ALL_RECOMMENDATIONS_CONTENT}`] || [];
}
export function getManualActivities(): RecommendationContext[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_MANUAL_ACTIVITIES}`] || [];
}
export function addManualActivity(value: RecommendationContext): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.ADD_MANUAL_ACTIVITY}`, {
    ...value,
    originalId: value.id,
    // id: v4(),
  });
}
export function dropManualActivities(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_MANUAL_ACTIVITIES}`);
}
export function getSolutionForTrip(tripId: string): ISolution | null {
  const myTrips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = myTrips.find((x) => x.trip.id === tripId);
  return trip?.solutionResponse?.solution || null;
}
export function removePOIFromSolution(activityId: string, isPoi = false): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.REMOVE_POI}`, {activityId, isPoi});
}
export function loadSourcePoi(sourceId: string): Promise<RecommendationContext> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.LOAD_SOURCE_POI}`, sourceId);
}
export function rescheduleActivity(request: IRescheduleActivityRequest): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.RESCHEDULE_ACTIVITY}`, request);
}
export function getSourcePoi(sourceId: string): RecommendationContext | null {
  const pois: RecommendationContext[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_SOURCE_POI}`];
  if (pois.length && pois[0]) {
    return pois.find((x) => x.id === sourceId) || null;
  }
  return null;
}
const currencyFilter = (value: CurrencyCode): string => {
  return value === CurrencyCode.AMEX_POINT ? 'Points' : value.toString();
};

export function getActivitiesPricesUpdates(tripId: string): Record<string, PriceWithExplanation> {
  const trips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = trips.find((x) => x.trip.id === tripId);
  if (!trip?.solutionResponse?.solution) {
    return {};
  }
  const currentSolution = getAmexSolution();
  const originalSolution = trip.solutionResponse.solution;
  if (currentSolution?.id === originalSolution?.id) {
    return (
      (trip.solutionUpdates || [])
        // .concat({
        //   activity_id: '8f408b70-0ee1-4b0a-83b9-7f71d1ea8497',
        //   updated_inventory: false,
        //   updated_price: {
        //     amount: 300,
        //     currency: CurrencyCode.USD,
        //   }
        // })

        .reduce((res: Record<string, PriceWithExplanation>, x: SolutionUpdateDetails) => {
          const activity = (originalSolution.activities || []).find((z) => z.id === x.activity_id);
          if (!activity) {
            return res;
          }
          const price = (activity.prices || []).find((z) => z.currency === x.updated_price?.currency || false);
          if (price && price.currency === CurrencyCode.USD) {
            const diff = x.updated_price.amount - price.amount;
            res[x.activity_id] = {
              amount: diff,
              value: x.updated_price.amount,
              currency: x.updated_price.currency,
              text: `Price ${diff > 0 ? 'Increased' : 'Descreased'} ${Math.abs(diff)} ${currencyFilter(
                price.currency,
              )}`,
            };
          }
          return res;
        }, {})
    );
  } else {
    const oldAccomodationActivities = originalSolution?.activities.filter(
      (x) => x.category === UserActivityCategoryEnum.ACCOMMODATION,
    );
    const newAccomodationActivities = currentSolution?.activities.filter(
      (x) => x.category === UserActivityCategoryEnum.ACCOMMODATION,
    );
    const firstNewActivityWithUSD = newAccomodationActivities?.find((x) => {
      return x.prices?.some((y) => y.currency === CurrencyCode.USD);
    });
    if (!firstNewActivityWithUSD) {
      return {};
    }
    const firstActivityUSDPrice = firstNewActivityWithUSD.prices.find((x) => x.currency === CurrencyCode.USD);

    const oldUSPrice = oldAccomodationActivities
      ?.map((x) => x.prices?.filter((y) => y.currency === CurrencyCode.USD))
      .filter(Boolean)
      .flat()
      .find((x) => x?.amount);
    const newUSPrice = newAccomodationActivities
      ?.map((x) => x.prices?.filter((y) => y.currency === CurrencyCode.USD))
      .filter(Boolean)
      .flat()
      .find((x) => x?.amount);
    const diff = Math.floor((newUSPrice?.amount || 0) - (oldUSPrice?.amount || 0));
    return {
      [firstNewActivityWithUSD.id]: {
        amount: diff,
        value: (firstActivityUSDPrice && firstActivityUSDPrice.amount) || 0,
        currency: CurrencyCode.USD,
        text: `$${Math.abs(diff)} ${diff > 0 ? 'more' : 'less'}`,
      },
    };
  }
}

/**
 * Returns dictionary of unavailable acitivites : {id: true means unavailable}
 * @param tripId String
 */
export function getActivitiesAviability(tripId: string): Record<string, boolean> {
  const trips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = trips.find((x) => x.trip.id === tripId);
  if (!trip?.solutionResponse?.solution) {
    return {};
  }
  const solution = trip.solutionResponse.solution;
  return (
    (trip.solutionUpdates || [])
      // .concat({
      //   activity_id: '8f408b70-0ee1-4b0a-83b9-7f71d1ea8497',
      //   updated_inventory: false,
      //   updated_price: {
      //     amount: 800,
      //     currency: CurrencyCode.USD,
      //   }
      // })
      .reduce((res: Record<string, boolean>, x: SolutionUpdateDetails) => {
        res[x.activity_id] = !x.updated_inventory;

        if (!x.updated_inventory) {
          const accommodation = (solution.activities || []).find(
            (y) => y.id === x.activity_id && y.category === UserActivityCategoryEnum.ACCOMMODATION,
          );
          if (accommodation) {
            (solution.activities || [])
              .filter((z) => {
                return z.start_location === z.end_location && z.end_location === accommodation.start_location;
              })
              .forEach((z) => (res[z.id] = true));
          }
        }
        return res;
      }, {})
  );
}
export function isSolutionVersionsAvailable(tripId: string): boolean {
  const solutionVersions = store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_VERSIONS}`];
  return Boolean(solutionVersions[tripId]);
}
export function getSolutionVersions(tripId: string): boolean {
  const solutionVersions = store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_VERSIONS}`];
  return solutionVersions[tripId] || [];
}
export function getOriginalSolutionId(tripId: string): string {
  const myTrips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = myTrips.find((x) => x.trip.id === tripId);
  const originalSolution = trip?.solutionResponse?.solution;
  return originalSolution?.id || '';
}
export function getOriginalSolution(tripId: string): ISolution | null {
  const myTrips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = myTrips.find((x) => x.trip.id === tripId);
  const originalSolution = trip?.solutionResponse?.solution || null;
  return originalSolution;
}
export function getOriginalSolutionSummary(tripId: string): SolutionSummary | null {
  const myTrips: SavedTrip[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_MY_TRIPS}`];
  const trip = myTrips.find((x) => x.trip.id === tripId);
  const originalSolution = trip?.solutionResponse?.destination || null;
  return originalSolution;
}
export function isRecommendationsReady(): boolean {
  const response = store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION}`];
  return Boolean(response);
}
export function isAdditionalRecommendationsDiningInputRequired(): boolean {
  return false;
  // TODO: remove this?
  // const response: RecommendationsResponse =
  //   store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_RECOMMENDATIONS}`];
  // return (isRecommendationsReady() && response?.need_additional_dining_inputs) || false;
}
export function isAdditionalRecommendationsExploreInputRequired(): boolean {
  return false;
  // TODO: remove this?
  // const response: RecommendationsResponse =
  //   store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_RECOMMENDATIONS}`];
  // return (isRecommendationsReady() && response?.need_additional_experience_inputs) || false;
}
export function isAdditionalInputForRecommendationsRequired(): boolean {
  return isAdditionalRecommendationsDiningInputRequired() || isAdditionalRecommendationsExploreInputRequired();
}
export function getSolutionRecommendations(filterValue = 'All'): Activity[] {
  const allowedPois = new Set([UserActivityCategoryEnum.EXPERIENCE, UserActivityCategoryEnum.DINING]);
  const response: Activity[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_RECOMMENDATIONS}`];
  return (response || [])
    .filter((x: Activity) => {
      return allowedPois.has(x.category);
    })
    .filter((x: Activity) => {
      if (filterValue === 'All') {
        return true;
      }
      return x.category === filterValue;
    });
}
export function getRecommendationsBlackList(): Set<string> {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_RECOMMENDATIONS_BLACKLIST}`];
}
export function setTripPrice(price: Price[]): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_TRIP_PRICE}`, price);
}
export function getTripPrice(): Price[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_PRICE}`];
}
export function getThemeTripWithSelectedPois(tripId: string): TripWithPois | null {
  const all: TripWithPois[] = store.getters[`${APP_MODULE}/${StoreGetters.GET_THEME_TRIPS}`];
  return all.find((x) => x.trip.id === tripId) || null;
}
export function getTripSuggestions(): SuggestionGroup[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_THEMES}`];
}
export function updateSolutionBudget(budget: BudgetFilterValue): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTION_BUDGET}`, budget);
}
export function updateGroupBudget(budget: BudgetFilterValue): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_GROUP_BUDGET}`, budget);
}
export function updateSolutionTravelers(travelers: {adults: string; children: string; rooms: string}): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTION_TRAVELERS}`, travelers);
}
export function updateDestBudget(budget: BudgetFilterValue): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_DEST_BUDGET}`, budget);
}
export function getTravelers(): TravelersFilterValue {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRAVELERS}`];
}
export function getTravelersInformation(): TravelersIndormation {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRAVELERS_INFORMATION}`];
}
export function getTravelersString(): string[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRAVELERS_STRING}`];
}
export function getBudget(route: ProblmSubmissionRoute) {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_BUDGET}`](route);
}
export function getPriceBudget(route: ProblmSubmissionRoute) {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_PRICE_BUDGET}`](route);
}
export function updateSolutionImg(url: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTION_IMG}`, url);
}
export function getSolutionImg(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_IMG}`];
}
export function updateDestName(name: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_DEST_NAME}`, name);
}
export function getDestName(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DEST_NAME}`];
}
export function getTripType(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_TYPE}`];
}
export function getTripCategory(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_CATEGORY}`];
}
export function updateTripCategory(value: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_TRIP_CATEGORY}`, value);
}
export function updateTripFocus(focus: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_TRIP_FOCUS}`, focus);
}
export function updateBudgetPreferences(budget: BudgetPreferences): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_BUDGET_PREFERENCES}`, budget);
}
export function getTripFocus(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_FOCUS}`];
}
export function getTripHighlights(): CandidatePoiItem[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_TRIP_HIGHLIGHTS}`];
}
export function getTripHighlightsName(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_HIGHLIGHTS_NAME}`];
}
export function updateSolutionDates(start: number, end: number): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTION_DATES}`, {start, end});
}
export function updateSolutionFilters(filters: FilterTag[]): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SELECTED_SOLUTION_FILTERS}`, filters);
}
export function getAllSolutionFilters(): FilterTag[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_ACTIVE_SOLUTION_FILTERS}`];
}
export function getIntersectionalFilters(): IntersectionalFiltersDomain {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_INTERSECTIONAL_FILTERS}`];
}
export function updateIntersectionFilter(
  category: UserActivityCategoryEnum,
  filter: IntersectionalFilter,
): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_INTERSECTIONAL_FILTERS}`, {
    category,
    filter,
  });
}

export function getSolutionSustanability(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_IS_SOLUTION_SUSTANABLE}`];
}
export function dropSolution(soft = false): Promise<ExtendedSolutionResponse> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.DROP_SOLUTION}`, soft);
}
export function updateTripContext(context: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_TRIP_CONTEXT}`, context);
}
export function getSolutionTimezone(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SOLUTION_TIMEZONE}`];
}
export function setAvailableTripOptions(options: Trip[]): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_AVAILABLE_TRIP_OPTIONS}`, options);
}
export function getAvailableTripThemes(): Trip[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_AVAILABLE_TRIP_OPTIONS}`];
}

export function selectTheme(value: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_SELECTED_THEME}`, value);
}
export function getSelectedTheme(): string {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_SELECTED_THEME}`];
}
export function setSolution(value: ExtendedSolutionResponse): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_SOLUTION}`, value);
}
export function rescheduleSolution(start: number, end: number): Promise<SolutionResponse | null> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.RESCHEDULE_SOLUTION}`, {start, end});
}
export function restoreTrip(problem: AmexProblem, tripName: string, tripId: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.RESTORE_TRIP}`, {problem, tripName, tripId});
}
export function updateSolutionName(tripName: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_SOLUTION_NAME}`, tripName);
}
export function blackListRecommendations(poiId: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.BLACK_LIST_RECOMMENDATION}`, poiId);
}
export function getMissingFilters(): FilterTag[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_MISSING_FILTERS}`];
}
export function removeMissingFilter(id: string): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.REMOVE_MISSING_FILTER}`, id);
}
export function dropMissingFilters(): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_MISSING_FILTERS}`, {filters: []});
}
export function setMissingFilters(
  filters: FilterTag[],
  travelersFilter?: TravelersIndormation,
  budget: Price[] = [],
): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.SET_MISSING_FILTERS}`, {
    filters,
    travelersFilter,
    budget,
  });
}
export function bookActivity(activityId: string, status: boolean): Promise<void> {
  return store.dispatch(`${APP_MODULE}/${StoreActions.UPDATE_BOOKING_STATUS}`, {activityId, status});
}
export function isActivityBooked(activityId: string): boolean {
  const booked = store.getters[`${APP_MODULE}/${StoreGetters.GET_BOOKED_ACTIVITIES}`];
  return booked[activityId] || false;
}
export function getDefaultImages(): FallbackImgs {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DEFAULT_IMAGES}`];
}
export function getDisplayFlights(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DISPLAY_FLIGHTS}`];
}
export function getDisplayHotels(): boolean {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DISPLAY_HOTELS}`];
}
export function getDiningActivities(): Activity[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_DINING_ACTIVITIES}`];
}
export function getExperienceActivities(): Activity[] {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_EXPERIENCE_ACTIVITIES}`];
}
export function getBudgetPreferences(): BudgetPreferences {
  return store.getters[`${APP_MODULE}/${StoreGetters.GET_BUDGET_PREFERENCES}`];
}
// re-export slices
export {ChatHistoryModule} from '../modules/index';
